import { useEffect, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import Menu from '@/components/Menu';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import candidatesServices, {
  ExpertiseParamsType,
  ExpertiseResponseType,
  MutateExpertiseParams,
} from '@/services/candidates';
import TransparentButton from '@/trio-ui/TransparentButton';
import { ExpertiseType } from '@/types';
import { yearsOfExperienceOptions } from '@/utils/dropdownOptions';
import CandidateExpertiseComponent from './CandidateExpertise.component';
import DialogBox from '../DialogBox';
import SectionBox from '../SectionBox';

type CandidateExpertiseProps = {
  defaultExpertises: ExpertiseType[];
  refetchCandidateData: () => void;
};

type EditingExpertiseType = {
  id?: string;
  skillId?: string;
  skill?: {
    id: string;
    name: string;
  };
  experience: string;
  experienceRange?: string;
  description: string;
  tags: string[];
  subSkills?: string[];
  isMainStack: boolean;
  isEditing?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

const defaultCandidateExpertise = {
  id: '',
  skill: {
    id: '',
    name: '',
  },
  experience: yearsOfExperienceOptions[0],
  tags: [],
  subSkills: [],
  description: '',
  isMainStack: false,
  isEditing: true,
};

const CandidateExpertise = ({
  defaultExpertises,
  refetchCandidateData,
}: CandidateExpertiseProps): JSX.Element => {
  const { id } = useParams();
  const personId = id ?? '';

  const [expertiseItems, setExpertiseItems] = useState<EditingExpertiseType[]>([]);
  const [expertiseIndexToDelete, setExpertiseIndexToDelete] = useState('');

  const toast = useToastContext();

  const { extractFromResume, getTechnologieMatch, isLoading } = useAutofillFromResume(
    personId as string
  );

  const getNormalizedExpertiseToSend = (editingExpertise: EditingExpertiseType): any => {
    let expertiseNormalized = editingExpertise;

    expertiseNormalized = _.omit(expertiseNormalized, ['isEditing', 'skill', 'subSkills']);

    if (expertiseNormalized.id) {
      expertiseNormalized = _.omit(expertiseNormalized, [
        'id',
        'skillId',
        'experienceRange',
        'createdAt',
        'updatedAt',
      ]);
    }

    return {
      ...expertiseNormalized,
      description: expertiseNormalized?.description || '',
    };
  };

  const getNormalizedExpertiseToReceive = (persistedExpertise: any): EditingExpertiseType => {
    const expertiseNormalized = persistedExpertise;

    _.omit(expertiseNormalized, ['createdAt', 'updatedAt', 'person', 'personSkillTags']);
    _.omit(expertiseNormalized.skill, ['createdAt', 'updatedAt']);

    return {
      ...expertiseNormalized,
      subSkills: expertiseNormalized.subSkills.map((subSkill) => subSkill.skill.id),
    };
  };

  useEffect(() => {
    if (!expertiseItems.length) {
      setExpertiseItems([defaultCandidateExpertise]);
    }
  }, [expertiseItems]);

  useEffect(() => {
    if (defaultExpertises.length) {
      const expertisesNormalized = defaultExpertises.map((expertise) =>
        getNormalizedExpertiseToReceive(expertise)
      );

      setExpertiseItems(expertisesNormalized);
    }
  }, [defaultExpertises]);

  const createExpertiseMutation = useApiSubmit<Array<ExpertiseResponseType>, MutateExpertiseParams>(
    candidatesServices.addNewExpertise
  );

  const updateExpertiseMutation = useApiSubmit<Array<ExpertiseResponseType>, MutateExpertiseParams>(
    candidatesServices.updateExpertise
  );

  const deleteExpertiseMutation = useApiSubmit<null, ExpertiseParamsType>(
    candidatesServices.deleteExpertise
  );

  const onAddNewExpertise = (): void => {
    setExpertiseItems((prev) => prev.concat(defaultCandidateExpertise));
  };

  const onDeleteExpertise = async (index: number): Promise<void> => {
    try {
      const currentExpertise = expertiseItems[index];

      if (currentExpertise.id) {
        const params = {
          expertiseId: currentExpertise.id,
        };

        await deleteExpertiseMutation.mutateAsync(params);
      }

      setExpertiseItems((prev) => prev.filter((p, i) => i !== index));
      setExpertiseIndexToDelete('');

      toast.showSuccessFeedback('The expertise has been deleted.');
      refetchCandidateData();
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the expertise.');
    }
  };

  const onEditExpertise = (index: number): void => {
    setExpertiseItems((prev) =>
      prev.map((expertise, i) => {
        if (i !== index) {
          return expertise;
        }
        return {
          ...expertise,
          isEditing: true,
        };
      })
    );
  };

  const onSaveExpertise = async (values: any, index: number): Promise<void> => {
    try {
      const currentExpertise = expertiseItems[index];

      const newExpertise = {
        ...currentExpertise,
        ...values,
      };

      const isEditing = !!currentExpertise?.id;

      const mutationToCall = isEditing ? updateExpertiseMutation : createExpertiseMutation;

      const params = {
        personId,
        expertiseId: currentExpertise.id ?? '',
        ...getNormalizedExpertiseToSend(newExpertise),
      };

      const mutationResponse = await mutationToCall.mutateAsync(params);
      const persistedExpertise = isEditing ? mutationResponse : mutationResponse[0];

      setExpertiseItems((prev) =>
        prev.map((expertise, i) => {
          if (i !== index) {
            return expertise;
          }

          return {
            ...getNormalizedExpertiseToReceive(persistedExpertise),
            isEditing: false,
          };
        })
      );

      toast.showSuccessFeedback('The expertise has been saved.');
      refetchCandidateData();
    } catch (error) {
      toast.showErrorFeedback('There was a problem to save the expertise.');
    }
  };

  const onCancelExpertiseEdition = (index: number): void => {
    const currentExpertise = expertiseItems[index];

    if (currentExpertise.id) {
      setExpertiseItems((prev) =>
        prev.map((expertise, i) => {
          if (i !== index) {
            return expertise;
          }

          return {
            ...expertise,
            isEditing: false,
          };
        })
      );
    } else {
      setExpertiseItems((prev) => prev.filter((p, i) => i !== index));
    }
  };

  const updateIndexToDelete = (index: number): void => {
    setExpertiseIndexToDelete(index.toString());
  };

  const handleAutofillFromResume = async (
    e: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    e.stopPropagation();

    try {
      const skills = await extractFromResume('skills');

      const expertiseList = typeof skills === 'string' ? JSON.parse(skills) : skills;

      if (!Array.isArray(expertiseList)) {
        throw new Error('Skills data is not in the expected format');
      }

      const formattedExpertises = expertiseList
        .map(
          (expertise: {
            name: string;
            experience: string;
            description: string;
            isMainStack: boolean;
            relatedTechnologies: string[];
          }) => {
            const matchingTech = getTechnologieMatch(expertise.name);

            if (!matchingTech) return null;

            // Format to match the structure of manually created expertises
            return {
              skillId: matchingTech,
              experience:
                expertise.experience === 'not provided'
                  ? yearsOfExperienceOptions[0]
                  : expertise.experience,
              tags: (expertise.relatedTechnologies || [])
                .map((techName) => getTechnologieMatch(techName))
                .filter(Boolean),
              description: expertise.description || '',
              isMainStack: expertise.isMainStack || false,
              isEditing: false,
            };
          }
        )
        .filter(Boolean);

      const persistedExpertises = await Promise.all(
        formattedExpertises.map(async (expertise) => {
          const params = {
            personId,
            expertiseId: '',
            ...expertise,
          };

          const mutationResponse = await createExpertiseMutation.mutateAsync(
            params as MutateExpertiseParams
          );
          const persistedExpertise = mutationResponse[0];
          return persistedExpertise;
        })
      );

      setExpertiseItems((prev) => [
        ...prev,
        ...(persistedExpertises.map(getNormalizedExpertiseToReceive) as EditingExpertiseType[]),
      ]);

      toast.showSuccessFeedback('Skills have been imported from resume.');
      refetchCandidateData();
    } catch (error) {
      toast.showErrorFeedback(
        'Failed to import skills from resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  return (
    <>
      <SectionBox
        title="Expertise"
        leftElement={
          isLoading ? (
            <Spinner size="xs" />
          ) : (
            <TransparentButton style={{ height: '24px' }} isBlueButton onClick={onAddNewExpertise}>
              + Add skill
            </TransparentButton>
          )
        }
        rightElement={
          <Menu
            icon={MoreHorizIcon}
            menuId="autofill-expertise-from-resume-button"
            menuItems={[
              {
                label: 'Autofill from Resume',
                menuItemIcon: AutoFixHigh,
                callback: handleAutofillFromResume,
              },
            ]}
          />
        }
      >
        <>
          {expertiseItems.map((expertise, index) => {
            return (
              <CandidateExpertiseComponent
                key={index.toString()}
                skillId={expertise.skill?.id}
                skillName={expertise.skill?.name}
                subSkills={expertise.subSkills ?? []}
                {...expertise}
                onDeleteExpertise={() => updateIndexToDelete(index)}
                onEditExpertise={() => onEditExpertise(index)}
                onSaveExpertise={(values) => onSaveExpertise(values, index)}
                onCancelExpertiseEdition={() => onCancelExpertiseEdition(index)}
              />
            );
          })}
          <TransparentButton
            style={{ height: '24px', marginTop: '30px' }}
            isBlueButton
            onClick={onAddNewExpertise}
          >
            + Add skill
          </TransparentButton>
        </>
      </SectionBox>

      <DialogBox
        isOpen={!!expertiseIndexToDelete}
        onCancelHandler={() => setExpertiseIndexToDelete('')}
        onConfirmHandler={() => onDeleteExpertise(Number(expertiseIndexToDelete))}
      />
    </>
  );
};

export default CandidateExpertise;
